import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertProps,
  Box,
  Button,
  ButtonProps,
  Container,
  Header,
  FormField,
  InputProps,
  Modal,
  SelectProps,
  SpaceBetween,
  Tiles,
} from "@amzn/awsui-components-react";
import { useOmsIdentity } from "../../hooks/omsContextIdentity";
import {
  OmsPresetRequest,
  OmsPresetScope,
  OmsProduct,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import useOmsExport, { restoredOmsExportMode } from "../../hooks/useOmsExport";
import useOmsFilterCriteria, {
  OmsFilterDraft,
} from "../../hooks/useOmsFilterCriteria";
import useOmsModalHeight from "../../hooks/useOmsModalHeight";
import useOmsPresets, {
  presetErrorMessage,
  presetValidationSourceUnavailable,
  useOmsExportCapabilities,
} from "../../hooks/useOmsPresets";
import { PresetRetryExpiredError } from "../../hooks/omsPresetRetry";
import {
  hasPendingOmsRequest,
  pendingOmsMutation,
} from "../../hooks/omsDraftStorage";
import OMSColumns, {
  columnSelectionError,
  OmsColumnsDraft,
} from "./OMSColumns";
import OMSExportPanel, { OMSExportActions } from "./OMSExportPanel";
import OMSExportSource from "./OMSExportSource";
import OMSWorkflow, {
  OMSSettingsSection,
  OMSWorkflowSection,
} from "./OMSWorkflow";
import OMSPresetToolbar from "./OMSPresetToolbar";
import OMSSettingsSummary from "./OMSSettingsSummary";
import OMSExportReview from "./OMSExportReview";
import {
  filterIssue,
  OmsFilterIssue,
  presetNameError,
} from "./omsConfigurationValidation";
import { submittedCondition } from "./omsFilterEditing";
import OMSPresetChanges, { OMSPresetContents } from "./OMSPresetChanges";
import { OMSPresetManager, OMSPresetSave } from "./OMSPresetEditor";
import OMSRecordFilters from "./OMSRecordFilters";
import { OMSHelpLink } from "./OMSContextHelp";
import {
  applyPreset,
  exportSettings,
  OmsConfiguration,
  OmsExportSettings,
  presetIsEdited,
  restorePresetSettings,
  suggestedPresetScope,
} from "./omsPresetEditing";

export default function OMSExportWorkspace({
  product,
  productLabel,
  report,
  availableReport,
  onClose,
  draft,
  onSaveDraft,
  resumeExport = true,
  page,
  initialJob,
}: {
  product: OmsProduct;
  productLabel: string;
  report: OmsReport;
  availableReport: OmsReport;
  onClose: (draft: OmsFilterDraft) => void;
  draft?: OmsFilterDraft;
  onSaveDraft: (draft: OmsFilterDraft) => void;
  resumeExport?: boolean;
  initialJob?: { jobId: string; mode: "full" | "filtered" };
  page?: {
    view: "export" | "manage" | "edit";
    presetId?: string;
    review: boolean;
    admission: boolean;
    onNavigate: (
      view: "export" | "manage" | "edit",
      presetId?: string,
      review?: boolean
    ) => void;
    onDirty: (dirty: boolean) => void;
    onJob: (jobId: string | null) => void;
    onNotify?: (message: string) => void;
    onBeginExport?: () => void;
    onCopy?: (presetId: string) => void;
  };
}) {
  const { namespace, financialAccount } = useOmsIdentity(product);
  const [manualEdits, setManualEdits] = useState(false);
  const [presetReplacement, setPresetReplacement] = useState<() => void>();
  const [mode, setMode] = useState<"full" | "filtered">(() => {
    if (initialJob) return initialJob.mode;
    if (draft?.viewingPrevious) return draft.viewingPrevious.mode;
    if (draft) return draft.tab === "filtered" ? "filtered" : "full";
    return resumeExport
      ? restoredOmsExportMode(product, report, namespace)
      : "full";
  });
  const [focusFilters, setFocusFilters] = useState(false);
  const [focusColumns, setFocusColumns] = useState(false);
  const [workspace, setWorkspace] = useState<"rows" | "columns">(
    draft?.workspace || "rows"
  );
  const [columns, setColumns] = useState<OmsColumnsDraft>(
    draft?.columns
      ? {
          ...draft.columns,
          customOrder:
            draft.columns.customOrder ?? Boolean(draft.columns.names.length),
        }
      : { strategy: "all", names: [], customOrder: false }
  );
  const [applied, setApplied] = useState(draft?.appliedPreset);
  const [presetBaseline, setPresetBaseline] = useState<
    OmsExportSettings | undefined
  >(
    () =>
      draft?.presetBaseline ||
      (draft?.appliedPreset
        ? exportSettings({
            mode,
            criteria: draft.criteria,
            operation: draft.operation,
            columns,
          })
        : undefined)
  );
  const [undo, setUndo] = useState<OmsConfiguration | undefined>(draft?.undo);
  const [announcement, setAnnouncement] = useState("");
  const [restoredNotice, setRestoredNotice] = useState(
    hasPendingOmsRequest(draft)
  );
  const [attempted, setAttempted] = useState(false);
  const [focusIssue, setFocusIssue] = useState<OmsFilterIssue>();
  const [reviewingExport, setReviewState] = useState(
    Boolean(page?.review && draft)
  );
  const visitedReview = useRef(Boolean(page?.review && draft));
  function setReviewingExport(value: boolean) {
    if (value) visitedReview.current = true;
    setReviewState(value);
    if (page) page.onNavigate("export", undefined, value);
  }
  useEffect(() => {
    if (!page) return;
    if (page.review && !visitedReview.current) page.onNavigate("export");
    else setReviewState(page.review);
    // Only the route controls this transition, not changing form values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.review]);
  const exportReviewRef = useRef<ButtonProps.Ref>(null);
  const reviewReturnFocus = useRef<"review" | "filters" | "columns">();
  useEffect(() => {
    if (reviewingExport || !reviewReturnFocus.current) return undefined;
    const timer = window.setTimeout(() => {
      // Let the replacement modal capture focus before focusing a settings control.
      if (reviewReturnFocus.current === "filters") setFocusFilters(true);
      else if (reviewReturnFocus.current === "columns") setFocusColumns(true);
      else exportReviewRef.current?.focus();
      reviewReturnFocus.current = undefined;
    }, 0);
    return () => window.clearTimeout(timer);
  }, [reviewingExport]);
  const [undoRequests, setUndoRequests] = useState(draft?.undoRequests);
  const [previousDraft, setPreviousDraft] = useState<
    OmsFilterDraft | undefined
  >(
    draft?.viewingPrevious
      ? { ...draft, viewingPrevious: undefined }
      : undefined
  );
  const [editor, setEditor] = useState<
    NonNullable<OmsFilterDraft["presetEditor"]>
  >(draft?.presetEditor || { view: "export", name: "", scope: "columns" });
  const [saveAttempted, setSaveAttempted] = useState(false);
  const [saveValidation, setSaveValidation] = useState("");
  const saveRef = useRef<ButtonProps.Ref>(null);
  const reviewPresetRef = useRef<ButtonProps.Ref>(null);
  const saveOrigin = useRef(editor.updating ? "update" : "new");
  const saveNames = useRef<{ existing: string; new: string }>({
    existing: applied?.name || "",
    new: "",
  });
  const saveScopes = useRef<{ existing: OmsPresetScope; new: OmsPresetScope }>({
    existing: editor.scope,
    new: editor.scope,
  });
  const presetReviewHeading = useRef<HTMLSpanElement>(null);
  const manageRef = useRef<ButtonProps.Ref>(null);
  const selectRef = useRef<SelectProps.Ref>(null);
  const nameRef = useRef<InputProps.Ref>(null);
  const saveErrorRef = useRef<AlertProps.Ref>(null);
  const editGeneration = useRef(0);
  const previousManualEdits = useRef(false);
  const capabilities = useOmsExportCapabilities();
  const presetsEnabled = Boolean(capabilities.data?.presets);
  const columnsEnabled = Boolean(capabilities.data?.columnSelection);
  const presets = useOmsPresets(product, presetsEnabled, draft?.presetRequest);
  const full = useOmsExport(
    product,
    draft?.report || report,
    "full",
    availableReport,
    (Boolean(draft?.viewingPrevious) || (resumeExport && !draft?.editing)) &&
      mode === "full",
    draft?.requests?.full ||
      (draft?.request?.mode === "full" ? draft.request : undefined),
    initialJob?.mode === "full" ? initialJob.jobId : undefined
  );
  const filtered = useOmsExport(
    product,
    draft?.report || report,
    "filtered",
    availableReport,
    (Boolean(draft?.viewingPrevious) || (resumeExport && !draft?.editing)) &&
      mode === "filtered",
    draft?.requests?.filtered ||
      (draft?.request?.mode === "filtered" ? draft.request : undefined),
    initialJob?.mode === "filtered" ? initialJob.jobId : undefined
  );
  const current = mode === "full" ? full : filtered;
  const source = current.sourceReport;
  const criteria = useOmsFilterCriteria(
    source,
    mode === "filtered" && !previousDraft ? current.job.data : undefined,
    draft
  );
  const active = Boolean(current.jobId) || current.create.isLoading;
  const accepted = current.job.data;
  useEffect(() => {
    if (
      announcement.startsWith("Saved ") &&
      active &&
      accepted?.status !== "READY"
    )
      setAnnouncement("");
  }, [announcement, active, accepted?.status]);
  const restored = useRef<string>();
  useEffect(() => {
    if (previousDraft || !accepted || restored.current === accepted.jobId)
      return;
    restored.current = accepted.jobId;
    setColumns((previous) => ({
      strategy: accepted.outputColumnStrategy || "all",
      customOrder: true,
      names:
        accepted.outputColumnStrategy === "selected"
          ? accepted.outputColumns || []
          : previous.names,
    }));
  }, [accepted, previousDraft]);
  const configuration: OmsConfiguration = {
    mode,
    criteria: criteria.criteria,
    operation: criteria.operation,
    columns,
    appliedPreset: applied,
    presetBaseline,
  };
  const configurationRef = useRef(configuration);
  configurationRef.current = configuration;
  const definitionEdited =
    (editor.view === "edit" || (editor.view === "save" && editor.updating)) &&
    (editor.name.trim().normalize("NFC") !== applied?.name ||
      editor.scope !== applied?.scope);
  const edited = Boolean(
    applied && (presetIsEdited(applied, configuration) || definitionEdited)
  );
  const columnError = columnSelectionError(columns, source);
  const validFilters = mode === "full" || criteria.valid;
  const previousRequest = current.matchesPreviousRequest({
    filters: mode === "full" ? [] : criteria.filters,
    operation: mode === "full" ? "and" : criteria.operation,
    outputColumns: columns.strategy === "selected" ? columns.names : undefined,
  });
  const validColumns =
    !columnError &&
    (columnsEnabled || columns.strategy === "all" || previousRequest);
  const preparing =
    current.create.isLoading ||
    (Boolean(current.jobId) &&
      !current.expired &&
      !current.unavailable &&
      !current.job.isError &&
      (!accepted || accepted.status === "PREPARING"));
  const { height, width, contentRef, titleRef, footerRef } = useOmsModalHeight(
    page && editor.view === "save"
      ? 880
      : editor.view === "manage" || editor.view === "save"
      ? 1000
      : 1520
  );
  const editingPreset = editor.view === "edit";
  useEffect(() => {
    if (current.jobId && current.create.isSuccess && editor.view === "edit")
      setEditor({ view: "export", name: "", scope: "columns" });
  }, [current.jobId, current.create.isSuccess, editor.view]);
  const issues =
    mode === "filtered"
      ? criteria.criteria
          .map((criterion, index) => filterIssue(source, criterion, index))
          .filter((issue): issue is OmsFilterIssue => Boolean(issue))
      : [];
  if (mode === "filtered" && !criteria.criteria.length)
    issues.push({ index: 0, control: "Field", message: "Choose a field." });
  const completeFilters =
    mode === "full"
      ? []
      : criteria.criteria
          .filter((criterion, index) => !filterIssue(source, criterion, index))
          .map(submittedCondition);

  function validateConfiguration() {
    setAttempted(true);
    if (issues.length) {
      if (!criteria.criteria.length) criteria.add();
      setWorkspace("rows");
      setFocusIssue(issues[0]);
      setFocusFilters(true);
      return false;
    }
    if (!validColumns) {
      setWorkspace("columns");
      setFocusColumns(true);
      return false;
    }
    return true;
  }
  function returnToDraft() {
    if (!previousDraft) return;
    const checkpoint = previousDraft;
    full.editSettings(checkpoint.report || report);
    filtered.editSettings(checkpoint.report || report);
    setMode(checkpoint.tab === "filtered" ? "filtered" : "full");
    criteria.replace(checkpoint.criteria, checkpoint.operation);
    setColumns(checkpoint.columns || { strategy: "all", names: [] });
    setApplied(checkpoint.appliedPreset);
    setPresetBaseline(
      checkpoint.presetBaseline ||
        (checkpoint.appliedPreset
          ? exportSettings({
              mode: checkpoint.tab === "filtered" ? "filtered" : "full",
              criteria: checkpoint.criteria,
              operation: checkpoint.operation,
              columns: checkpoint.columns || { strategy: "all", names: [] },
            })
          : undefined)
    );
    setWorkspace(checkpoint.workspace || "rows");
    full.restoreRetryIdentity(checkpoint.requests?.full);
    filtered.restoreRetryIdentity(checkpoint.requests?.filtered);
    setEditor(
      checkpoint.presetEditor || { view: "export", name: "", scope: "columns" }
    );
    setUndo(checkpoint.undo);
    setUndoRequests(checkpoint.undoRequests);
    setPreviousDraft(undefined);
    setAnnouncement("Editing draft restored.");
  }

  function changed() {
    setManualEdits(true);
    editGeneration.current += 1;
    setUndo(undefined);
    setUndoRequests(undefined);
    setAnnouncement("");
  }
  function restore(next: OmsConfiguration, nextSource = source) {
    // A reversible preset edit must retain an uncertain request's retry identity.
    if (full.jobId || full.sourceReport.snapshotId !== nextSource.snapshotId)
      full.editSettings(nextSource);
    if (
      filtered.jobId ||
      filtered.sourceReport.snapshotId !== nextSource.snapshotId
    )
      filtered.editSettings(nextSource);
    setMode(next.mode);
    criteria.replace(next.criteria, next.operation);
    setColumns(next.columns);
    setApplied(next.appliedPreset);
    setPresetBaseline(
      next.presetBaseline ||
        (next.appliedPreset ? exportSettings(next) : undefined)
    );
  }
  function undoSettings() {
    if (!undo) return;
    restore(undo);
    full.restoreRetryIdentity(undoRequests?.full);
    filtered.restoreRetryIdentity(undoRequests?.filtered);
    setUndo(undefined);
    setUndoRequests(undefined);
    setAttempted(false);
    setAnnouncement("Previous rows and columns restored.");
    window.setTimeout(() => selectRef.current?.focus(), 0);
  }
  function undoPresetChanges() {
    if (!applied) return;
    const restoredSettings = restorePresetSettings(
      applied,
      configuration,
      source
    );
    editGeneration.current += 1;
    restore(restoredSettings);
    setManualEdits(false);
    setUndo(undefined);
    setUndoRequests(undefined);
    setAttempted(false);
    setFocusIssue(undefined);
    setAnnouncement("Preset settings restored.");
    window.setTimeout(() => selectRef.current?.focus(), 0);
  }
  const selectPreset = async (id: string, editing = false) => {
    const generation = editGeneration.current;
    try {
      const preset = await presets.read.mutateAsync(id);
      if (generation !== editGeneration.current) {
        setAnnouncement(
          "Settings changed while the preset loaded. Choose it again to apply."
        );
        return undefined;
      }
      const previous = configurationRef.current;
      const previousRequests = {
        full: full.requestIdentity() || undefined,
        filtered: filtered.requestIdentity() || undefined,
      };
      const next = applyPreset(preset, previous, source);
      const apply = () => {
        restore(next);
        if (editing) previousManualEdits.current = manualEdits;
        setManualEdits(false);
        setUndo(previous);
        setUndoRequests(previousRequests);
        setAnnouncement(
          editing
            ? `Editing ${preset.name}. Review changes before saving.`
            : `Applied ${preset.name}.`
        );
        if (editing)
          setEditor({
            view: "edit",
            name: preset.name,
            scope: preset.scope,
            updating: true,
            previous,
            previousReport: source,
            previousWorkspace: workspace,
            previousRequests,
            cancelTo: editor.view === "manage" ? "manage" : "export",
          });
        if (columnSelectionError(next.columns, source)) {
          setWorkspace("columns");
          setFocusColumns(true);
        } else if (preset.scope !== "columns") {
          setWorkspace("rows");
          setFocusFilters(true);
        } else {
          setWorkspace("columns");
          setFocusColumns(true);
        }
      };
      const replacesRows =
        preset.scope !== "columns" &&
        (JSON.stringify(exportSettings(previous).criteria) !==
          JSON.stringify(exportSettings(next).criteria) ||
          previous.mode !== next.mode ||
          previous.operation !== next.operation);
      const replacesColumns =
        preset.scope !== "filters" &&
        JSON.stringify(previous.columns) !== JSON.stringify(next.columns);
      if (page && manualEdits && !editing && (replacesRows || replacesColumns))
        setPresetReplacement(() => apply);
      else apply();
      return preset;
    } catch {
      return undefined;
    }
  };
  const requestPreset = (id: string, editing = false) => {
    selectPreset(id, editing).catch(() => undefined);
  };
  const openSave = (
    updating = false,
    returnTo: "save" | "actions" | "edit" = "save"
  ) => {
    saveOrigin.current = updating ? "update" : "new";
    presets.change.reset();
    presets.read.reset();
    setSaveValidation("");
    setSaveAttempted(false);
    let scope: OmsPresetScope = "columns";
    let name = "";
    if (updating && applied)
      scope =
        editor.view === "edit" && editor.scope !== applied.scope
          ? editor.scope
          : suggestedPresetScope(applied, configuration);
    else if (validFilters && validColumns) scope = "both";
    if (updating && applied)
      name = editor.view === "edit" ? editor.name : applied.name;
    saveNames.current = {
      existing: editor.view === "edit" ? editor.name : applied?.name || "",
      new: "",
    };
    saveScopes.current = { existing: scope, new: scope };
    setEditor({
      ...editor,
      view: "save",
      name,
      scope,
      updating,
      returnTo,
      saveCheckpoint: {
        configuration,
        report: source,
        workspace,
        requests: {
          full: full.requestIdentity() || undefined,
          filtered: filtered.requestIdentity() || undefined,
        },
      },
    });
  };
  const backToExport = (restoreCheckpoint = true) => {
    if (restoreCheckpoint && editor.view === "save" && editor.saveCheckpoint) {
      const checkpoint = editor.saveCheckpoint;
      restore(checkpoint.configuration, checkpoint.report);
      setWorkspace(checkpoint.workspace);
      full.restoreRetryIdentity(checkpoint.requests?.full);
      filtered.restoreRetryIdentity(checkpoint.requests?.filtered);
    }
    setEditor({
      ...editor,
      name:
        editor.view === "save" && editor.returnTo === "edit" && !editor.updating
          ? saveNames.current.existing
          : editor.name,
      view: editor.returnTo === "edit" ? "edit" : "export",
    });
    window.setTimeout(() => {
      if (editor.view === "manage") manageRef.current?.focus();
      else if (saveOrigin.current === "update" && reviewPresetRef.current)
        reviewPresetRef.current.focus();
      else if (saveRef.current) saveRef.current.focus();
      else manageRef.current?.focus();
    }, 0);
  };
  const changeSaveDestination = (updating: boolean) => {
    saveNames.current[editor.updating ? "existing" : "new"] = editor.name;
    if (page)
      saveScopes.current[editor.updating ? "existing" : "new"] = editor.scope;
    setEditor({
      ...editor,
      updating,
      name: saveNames.current[updating ? "existing" : "new"],
      scope: page
        ? saveScopes.current[updating ? "existing" : "new"]
        : editor.scope,
    });
    setSaveValidation("");
    setSaveAttempted(false);
    if (page && updating)
      window.setTimeout(() => presetReviewHeading.current?.focus(), 0);
  };
  const cancelEditing = () => {
    if (editor.previous) {
      restore(editor.previous, editor.previousReport || source);
      if (editor.previousRequests) {
        full.restoreRetryIdentity(editor.previousRequests.full);
        filtered.restoreRetryIdentity(editor.previousRequests.filtered);
      }
    }
    if (editor.previousWorkspace) setWorkspace(editor.previousWorkspace);
    setEditor({
      view: editor.cancelTo || "export",
      name: "",
      scope: "columns",
    });
    setUndo(undefined);
    setAnnouncement("");
    setManualEdits(previousManualEdits.current);
    window.setTimeout(() => selectRef.current?.focus(), 0);
  };
  const savePreset = () => {
    setSaveAttempted(true);
    const name = editor.name.trim().normalize("NFC");
    const nameError = presetNameError(name);
    if (nameError) {
      setSaveValidation(editor.updating ? nameError : "");
      nameRef.current?.focus();
      return;
    }
    if (
      (editor.scope !== "columns" && !validFilters) ||
      (editor.scope !== "filters" && !validColumns)
    ) {
      setSaveValidation("Correct the settings included in this preset.");
      return;
    }
    const body: OmsPresetRequest = {
      name,
      scope: editor.scope,
      validationSnapshotId: source.snapshotId,
      ...(editor.scope !== "columns"
        ? {
            rows: mode === "full" ? "all" : "filtered",
            filters: mode === "full" ? [] : criteria.filters,
            operation: mode === "full" ? "and" : criteria.operation,
          }
        : {}),
      ...(editor.scope !== "filters"
        ? {
            columns:
              columns.strategy === "all"
                ? { strategy: "all" }
                : { strategy: "selected", names: columns.names },
          }
        : {}),
    };
    if (new Blob([JSON.stringify(body)]).size > 16 * 1024) {
      setSaveValidation(
        "These settings exceed the 16 KiB preset limit. You can still export them without saving."
      );
      return;
    }
    setSaveValidation("");
    presets.change
      .mutateAsync({
        action: "save",
        body,
        existing: editor.updating ? applied : undefined,
      })
      .then((result) => {
        if (result.preset) {
          setApplied(result.preset);
          setPresetBaseline(exportSettings(configuration));
        }
        setManualEdits(false);
        setAnnouncement(`Saved ${name}.`);
        page?.onNotify?.(`Saved preset ${name}.`);
        if (page && editor.returnTo === "edit") {
          cancelEditing();
          page.onDirty(false);
          page.onNavigate("manage");
          return;
        }
        setUndo(undefined);
        setEditor({ view: "export", name: "", scope: "columns" });
        window.setTimeout(() => {
          if (active) saveRef.current?.focus();
          else selectRef.current?.focus();
        }, 0);
      })
      .catch(() => nameRef.current?.focus());
  };
  useEffect(() => {
    if (
      editor.view === "save" &&
      (presets.change.isError || presets.read.isError)
    )
      saveErrorRef.current?.focus();
  }, [
    editor.view,
    presets.change.error,
    presets.change.isError,
    presets.read.error,
    presets.read.isError,
  ]);
  const retryRequest = current.requestIdentity();
  const editingDraft: OmsFilterDraft = {
    snapshotId: source.snapshotId,
    report: source,
    editing: !active,
    pendingExport:
      pendingOmsMutation(full.create) ||
      pendingOmsMutation(filtered.create) ||
      Boolean(
        draft?.pendingExport && full.create.isIdle && filtered.create.isIdle
      ),
    pendingPreset:
      pendingOmsMutation(presets.change) ||
      Boolean(
        draft?.pendingPreset &&
          presets.change.isIdle &&
          presets.changeIdentity()
      ),
    criteria: criteria.criteria,
    operation: criteria.operation,
    tab: mode,
    columns,
    workspace,
    appliedPreset: applied,
    presetBaseline,
    presetEditor: editor,
    request: retryRequest ? { mode, ...retryRequest } : undefined,
    presetRequest: presets.changeIdentity(),
    undo,
    undoRequests,
    requests: {
      full: full.requestIdentity() || undefined,
      filtered: filtered.requestIdentity() || undefined,
    },
  };
  const recoveryDraft: OmsFilterDraft = previousDraft
    ? { ...previousDraft, viewingPrevious: { mode } }
    : editingDraft;
  const presetSavePending = Boolean(editingDraft.pendingPreset);
  const serializedDraft = JSON.stringify(recoveryDraft);
  useEffect(() => {
    onSaveDraft(JSON.parse(serializedDraft) as OmsFilterDraft);
  }, [serializedDraft, onSaveDraft]);
  const close = () => {
    onClose(recoveryDraft);
  };
  const editableCriteria = {
    ...criteria,
    update: (...args: Parameters<typeof criteria.update>) => {
      changed();
      criteria.update(...args);
    },
    setOperator: (...args: Parameters<typeof criteria.setOperator>) => {
      changed();
      criteria.setOperator(...args);
    },
    setColumn: (...args: Parameters<typeof criteria.setColumn>) => {
      changed();
      criteria.setColumn(...args);
    },
    setOperation: (...args: Parameters<typeof criteria.setOperation>) => {
      changed();
      criteria.setOperation(...args);
    },
    add: () => {
      changed();
      criteria.add();
    },
    remove: (index: number) => {
      changed();
      criteria.remove(index);
    },
    clear: () => {
      changed();
      criteria.clear();
    },
  };
  const saveStatus = (
    presets.change.error as { response?: { status?: number } }
  )?.response?.status;
  const routeApplied = useRef<string>();
  useEffect(() => {
    if (!page) return;
    const route = `${page.view}:${page.presetId || ""}`;
    if (routeApplied.current === route) return;
    routeApplied.current = route;
    if (page.view === "manage") {
      if (editor.view === "edit") cancelEditing();
      setEditor({ view: "manage", name: "", scope: "columns" });
    } else if (page.view === "edit" && page.presetId)
      requestPreset(page.presetId, true);
    else if (editor.view === "edit") cancelEditing();
    else if (editor.view === "manage")
      setEditor((previous) => ({ ...previous, view: "export" }));
    // Route changes preserve the same controller and pinned source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page?.view, page?.presetId]);
  const onDirty = page?.onDirty;
  const onJob = page?.onJob;
  useEffect(() => {
    onDirty?.(!active && (manualEdits || (editingPreset && edited)));
  }, [active, manualEdits, editingPreset, edited, onDirty]);
  useEffect(() => {
    if (current.jobId) {
      setManualEdits(false);
      onJob?.(current.jobId);
    }
  }, [current.jobId, onJob]);
  const title =
    editor.view === "manage"
      ? `Manage ${productLabel} presets`
      : `Export ${productLabel} report`;

  const expiredRequest =
    presets.change.error instanceof PresetRetryExpiredError;
  let saveErrorHeader = "Could not save preset";
  if (expiredRequest) saveErrorHeader = "Review the earlier request";
  if (presets.read.isError) saveErrorHeader = "Could not reload preset";
  let saveErrorMessage = presetErrorMessage(
    presets.read.isError ? presets.read.error : presets.change.error
  );
  if (presetSavePending && !expiredRequest && !presets.read.isError)
    saveErrorMessage =
      "The save may have completed. Retry to check it with the same settings.";
  if (saveValidation) saveErrorMessage = saveValidation;
  let saveButtonLabel = editor.updating ? "Update preset" : "Create preset";
  if (presetSavePending) saveButtonLabel = "Retry save";
  let saveRecoveryAction: React.ReactNode;
  if (presets.change.error instanceof PresetRetryExpiredError) {
    if (presets.change.error.reconciled)
      saveRecoveryAction = (
        <Button
          onClick={() => {
            presets.restartExpiredRequest();
            setSaveValidation("");
            setSaveAttempted(false);
          }}
        >
          Start new save
        </Button>
      );
  } else if (
    saveStatus === 412 ||
    (saveStatus === 404 &&
      !presetValidationSourceUnavailable(presets.change.error))
  ) {
    saveRecoveryAction = (
      <SpaceBetween direction="horizontal" size="xs">
        {applied && (
          <Button
            loading={presets.read.isLoading}
            loadingText="Reloading preset"
            onClick={() => {
              selectPreset(applied.presetId)
                .then((reloaded) => {
                  if (!reloaded) return;
                  presets.change.reset();
                  setSaveValidation("");
                  backToExport(false);
                  setEditor((previous) => ({
                    ...previous,
                    name: reloaded.name,
                    scope: reloaded.scope,
                  }));
                })
                .catch(() => undefined);
            }}
          >
            Reload preset
          </Button>
        )}
        <Button
          disabled={presets.read.isLoading}
          onClick={() => {
            presets.change.reset();
            presets.read.reset();
            saveNames.current.existing = editor.name;
            setEditor({
              ...editor,
              updating: false,
              name: `${editor.name.slice(0, 75)} copy`,
            });
          }}
        >
          Set up a new preset
        </Button>
      </SpaceBetween>
    );
  }

  const prepareAllowed =
    (page?.admission ?? true) &&
    !presets.read.isLoading &&
    !presets.change.isLoading &&
    !(columns.strategy === "selected" && !columnsEnabled && !previousRequest);
  const exportActions = (
    <OMSExportActions
      state={current}
      reviewInDialog={!page}
      filters={mode === "full" ? [] : criteria.filters}
      operation={mode === "full" ? "and" : criteria.operation}
      outputColumns={
        columns.strategy === "selected" ? columns.names : undefined
      }
      valid={prepareAllowed}
      onReview={
        reviewingExport
          ? undefined
          : () => {
              if (validateConfiguration()) setReviewingExport(true);
            }
      }
      reviewRef={reviewingExport ? undefined : exportReviewRef}
      onPrepare={() => {
        const valid = validateConfiguration();
        if (valid) {
          setAnnouncement("");
          page?.onBeginExport?.();
        }
        if (reviewingExport) {
          if (valid) reviewReturnFocus.current = "review";
          else
            reviewReturnFocus.current = issues.length ? "filters" : "columns";
        }
        setReviewingExport(false);
        return valid;
      }}
      hideEditSettings={Boolean(previousDraft)}
      onConfigure={() => {
        page?.onJob(null);
        setReviewingExport(false);
        changed();
        if (mode === "filtered") {
          setWorkspace("rows");
          setFocusFilters(true);
        }
      }}
    />
  );
  if (reviewingExport)
    return (
      <OMSExportReview
        productLabel={productLabel}
        report={source}
        mode={mode}
        filters={completeFilters}
        operation={criteria.operation}
        columns={columns.strategy === "all" ? source.columns : columns.names}
        allColumns={columns.strategy === "all"}
        page={Boolean(page)}
        onBack={() => {
          reviewReturnFocus.current = "review";
          setReviewingExport(false);
        }}
        actions={exportActions}
      />
    );

  const sourceDetails = (
    <OMSExportSource
      report={source}
      availableReport={availableReport}
      accepted={active}
      compact
      showMetadata={!editingPreset}
      onUseLatest={() => {
        changed();
        if (previousDraft) {
          setPreviousDraft(undefined);
          setEditor({ view: "export", name: "", scope: "columns" });
        }
        const adoptedCriteria =
          active && accepted
            ? accepted.filters.map((filter, id) => ({
                ...filter,
                id,
              }))
            : criteria.criteria;
        if (active && accepted) {
          setMode(accepted.mode);
          setColumns({
            strategy: accepted.outputColumnStrategy || "all",
            names: accepted.outputColumns || [],
            customOrder: true,
          });
          setApplied(undefined);
          setPresetBaseline(undefined);
        }
        setAttempted(true);
        criteria.replace(
          adoptedCriteria.map((criterion) => {
            const previousType =
              source.columnTypes?.[criterion.column] || "string";
            const nextType =
              availableReport.columnTypes?.[criterion.column] || "string";
            return availableReport.columns.includes(criterion.column) &&
              previousType !== nextType
              ? {
                  ...criterion,
                  valueNotice: `This field changed from ${previousType} to ${nextType}. Review its condition and value.`,
                }
              : criterion;
          }),
          active && accepted ? accepted.operation : criteria.operation
        );
        full.editSettings(availableReport);
        filtered.editSettings(availableReport);
        if (mode === "filtered") {
          setWorkspace("rows");
          setFocusFilters(true);
        }
        const nextColumns: OmsColumnsDraft =
          active && accepted
            ? {
                strategy: accepted.outputColumnStrategy || "all",
                names: accepted.outputColumns || [],
                customOrder: true,
              }
            : columns;
        if (nextColumns.strategy === "selected") {
          setColumns({ ...nextColumns, touched: true });
          if (columnSelectionError(nextColumns, availableReport)) {
            setWorkspace("columns");
            setFocusColumns(true);
          }
        }
      }}
    />
  );
  const presetToolbar =
    presetsEnabled && !active && editor.view !== "save" ? (
      <OMSPresetToolbar
        state={presets}
        applied={applied}
        disabled={active}
        saveDisabled={active && accepted?.status !== "READY"}
        modified={edited}
        editing={editingPreset}
        page={Boolean(page)}
        grouped={Boolean(page)}
        onUndoPreset={undo && !editingPreset ? () => undoSettings() : undefined}
        onCustom={() => {
          changed();
          setApplied(undefined);
          setPresetBaseline(undefined);
          setEditor({ view: "export", name: "", scope: "columns" });
          selectRef.current?.focus();
        }}
        onApply={requestPreset}
        onSave={() => openSave(false, editingPreset ? "edit" : "actions")}
        onReview={() => openSave(true, editingPreset ? "edit" : "actions")}
        onResetChanges={() => undoPresetChanges()}
        onManage={() => {
          if (page) {
            page.onNavigate("manage");
            return;
          }
          presets.change.reset();
          setEditor({
            ...editor,
            view: "manage",
            returnTo: editingPreset ? "edit" : "actions",
          });
        }}
        saveRef={saveRef}
        reviewRef={reviewPresetRef}
        selectRef={selectRef}
        manageRef={manageRef}
      />
    ) : null;
  const Wrapper = page && editor.view !== "save" ? OMSWorkflow : Modal;
  const workflowStage = active ? "result" : "settings";
  let contextTitle = "Report and preset";
  if (editingPreset) contextTitle = "Preset";
  else if (active) contextTitle = "Source report";
  let pageHeading = "Create export";
  if (editingPreset) pageHeading = "Edit preset";
  else if (active) pageHeading = "Export result";
  let closeLabel = "Close report export";
  if (page && editor.view === "save")
    closeLabel = presetSavePending
      ? "Close and return to dashboard"
      : "Close preset dialog";
  const returnToPresetChanges = Boolean(
    page &&
      editor.view === "save" &&
      !editor.updating &&
      saveOrigin.current === "update" &&
      applied
  );
  let backLabel =
    editor.view === "save" ? "Back to settings" : "Back to export";
  if (returnToPresetChanges) backLabel = "Back to preset changes";
  return (
    <Wrapper
      heading={editor.view === "manage" ? undefined : pageHeading}
      focusHeading={Boolean(page && active)}
      stage={editor.view === "manage" ? undefined : workflowStage}
      completionLabel={editingPreset ? "Saved" : "Export"}
      parent={
        page && editingPreset
          ? {
              text: "Presets",
              href: `/dashboard/oms/${product}${
                financialAccount ? `/account/${financialAccount}` : ""
              }/presets`,
              onFollow: () => page.onNavigate("manage"),
            }
          : undefined
      }
      visible
      onDismiss={
        page && editor.view === "save"
          ? () => {
              if (!presetSavePending) backToExport();
              else {
                page.onDirty(false);
                close();
              }
            }
          : close
      }
      header={
        <span ref={titleRef}>
          {page && editor.view === "save"
            ? editor.updating
              ? `Update “${applied?.name || "preset"}”?`
              : "Save as a preset"
            : title}
        </span>
      }
      size="xx-large"
      width={width}
      position="top"
      height={page && editor.view === "save" ? undefined : height}
      closeAriaLabel={closeLabel}
      footer={
        page && editor.view === "manage" ? undefined : (
          <div ref={footerRef} className="oms-report-download__footer">
            {(editor.view === "export" || editingPreset) && (
              <>
                {!page &&
                  (previousDraft ||
                    editingPreset ||
                    (presetsEnabled &&
                      active &&
                      accepted?.status === "READY")) && (
                    <div className="oms-report-download__footer-context">
                      {previousDraft && (
                        <Button onClick={() => returnToDraft()}>
                          Return to draft
                        </Button>
                      )}
                      {editingPreset && (
                        <Button onClick={() => cancelEditing()}>
                          Cancel editing
                        </Button>
                      )}
                      {presetsEnabled &&
                        active &&
                        !previousDraft &&
                        accepted?.status === "READY" && (
                          <Button ref={saveRef} onClick={() => openSave()}>
                            Save as new preset
                          </Button>
                        )}
                    </div>
                  )}
                <div
                  className={`oms-report-download__footer-actions${
                    page && !editingPreset
                      ? " oms-report-download__footer-actions--export"
                      : ""
                  }`}
                  role="group"
                  aria-label="Export actions"
                >
                  {!page && (
                    <Button
                      variant={
                        current.jobId && preparing ? "primary" : "normal"
                      }
                      onClick={close}
                    >
                      Close
                    </Button>
                  )}
                  {page && editingPreset ? (
                    <>
                      <Button
                        onClick={() => {
                          cancelEditing();
                          page.onDirty(false);
                          page.onNavigate("manage");
                        }}
                      >
                        Cancel editing
                      </Button>
                      <Button
                        variant="primary"
                        disabled={!edited}
                        onClick={() => openSave(true, "edit")}
                      >
                        Review changes
                      </Button>
                    </>
                  ) : (
                    exportActions
                  )}
                </div>
              </>
            )}
            {(editor.view === "save" || editor.view === "manage") && (
              <>
                {(!page || editor.view === "save") && (
                  <Button
                    disabled={
                      presets.change.isLoading ||
                      presets.read.isLoading ||
                      presetSavePending
                    }
                    onClick={() => {
                      if (returnToPresetChanges) changeSaveDestination(true);
                      else backToExport();
                      if (page && editor.view === "manage")
                        page.onNavigate("export");
                    }}
                  >
                    {backLabel}
                  </Button>
                )}
                {page && editor.view === "save" && editor.updating && (
                  <Button
                    disabled={presets.change.isLoading || presetSavePending}
                    onClick={() => changeSaveDestination(false)}
                  >
                    Set up a new preset
                  </Button>
                )}
                {editor.view === "save" && (
                  <Button
                    variant="primary"
                    loading={presets.change.isLoading}
                    loadingText="Saving preset"
                    onClick={savePreset}
                    disabled={
                      (editor.updating && !edited) ||
                      presets.read.isLoading ||
                      (editor.scope !== "columns" && !validFilters) ||
                      (editor.scope !== "filters" && !validColumns)
                    }
                  >
                    {saveButtonLabel}
                  </Button>
                )}
              </>
            )}
          </div>
        )
      }
    >
      {presetReplacement && (
        <Modal
          visible
          size="medium"
          header="Discard changes?"
          closeAriaLabel="Close discard confirmation"
          onDismiss={() => setPresetReplacement(undefined)}
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => setPresetReplacement(undefined)}>
                  Keep editing
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    presetReplacement();
                    setPresetReplacement(undefined);
                  }}
                >
                  Discard changes
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          Applying this preset replaces the settings you changed.
        </Modal>
      )}
      <div ref={contentRef}>
        {editor.view === "manage" && (
          <OMSPresetManager
            confirmInModal={Boolean(page)}
            readOnly={Boolean(page && !page.admission)}
            state={presets}
            onEdit={(id) =>
              page ? page.onNavigate("edit", id) : requestPreset(id, true)
            }
            onCopy={page?.onCopy}
            onDeleted={(id) => {
              if (applied?.presetId === id) {
                setApplied(undefined);
                setPresetBaseline(undefined);
              }
              setUndo(undefined);
              setAnnouncement(
                "Preset deleted. Applied settings are preserved."
              );
              page?.onNotify?.("Preset deleted.");
            }}
            onRenamed={(metadata) => {
              if (applied?.presetId === metadata.presetId)
                setApplied({ ...applied, ...metadata });
              page?.onNotify?.(`Renamed preset to ${metadata.name}.`);
            }}
          />
        )}
        {editor.view !== "manage" && (
          <SpaceBetween size="m">
            {editor.view !== "save" &&
              (page ? (
                <OMSWorkflowSection title={contextTitle}>
                  {editingPreset && applied && (
                    <Box fontWeight="bold">{applied.name}</Box>
                  )}
                  {sourceDetails}
                  {presetToolbar}
                </OMSWorkflowSection>
              ) : (
                sourceDetails
              ))}
            {editor.view === "save" && (
              <Container
                header={
                  <Header variant="h3">
                    <span
                      ref={editor.updating ? presetReviewHeading : undefined}
                      tabIndex={editor.updating ? -1 : undefined}
                    >
                      {editor.updating
                        ? "Review preset changes"
                        : "Create a new preset"}
                    </span>
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  {(saveValidation ||
                    presets.change.isError ||
                    presets.read.isError) && (
                    <Alert
                      ref={saveErrorRef}
                      type={
                        expiredRequest && !presets.read.isError
                          ? "warning"
                          : "error"
                      }
                      header={saveErrorHeader}
                      action={saveRecoveryAction}
                    >
                      {saveErrorMessage}
                    </Alert>
                  )}
                  {presetSavePending &&
                    presets.change.isIdle &&
                    !presets.read.isError && (
                      <Alert type="info" header="Recover the earlier save">
                        The save may have completed. Retry to check it with the
                        same settings.
                      </Alert>
                    )}
                  {(!page || !editor.updating) && (
                    <OMSPresetSave
                      name={editor.name}
                      scope={editor.scope}
                      onName={(name) => setEditor({ ...editor, name })}
                      onScope={(scope: OmsPresetScope) =>
                        setEditor({ ...editor, scope })
                      }
                      validFilters={validFilters}
                      validColumns={validColumns}
                      attempted={saveAttempted}
                      nameRef={nameRef}
                      disabled={
                        presets.change.isLoading ||
                        presets.read.isLoading ||
                        presetSavePending
                      }
                      reviewing={editor.updating}
                      currentPresetName={page ? undefined : applied?.name}
                      onDestination={changeSaveDestination}
                    />
                  )}
                  {editor.updating && applied && (
                    <OMSPresetChanges
                      saved={applied}
                      draft={configuration}
                      name={editor.name}
                      scope={editor.scope}
                      report={source}
                    />
                  )}
                  {!editor.updating && (
                    <OMSPresetContents
                      collapsible={!page}
                      draft={configuration}
                      scope={editor.scope}
                      report={source}
                    />
                  )}
                </SpaceBetween>
              </Container>
            )}
            {editor.view !== "save" &&
              (current.jobId || !current.create.isIdle) && (
                <OMSExportPanel state={current} mode={mode} />
              )}
            {editor.view === "export" && !active && restoredNotice && (
              <Alert
                type="info"
                dismissible
                dismissAriaLabel="Dismiss restored draft notice"
                onDismiss={() => setRestoredNotice(false)}
              >
                The earlier request may have completed. Your settings are
                retained so you can retry safely.
              </Alert>
            )}
            {!page && presetToolbar}
            {editor.view !== "save" &&
              (!active || accepted?.status === "READY") &&
              announcement &&
              !announcement.startsWith("Applied ") &&
              !announcement.startsWith("Editing ") &&
              !(page && announcement.startsWith("Saved ")) && (
                <Alert
                  type={announcement.startsWith("Saved ") ? "success" : "info"}
                  dismissible
                  dismissAriaLabel="Dismiss preset notice"
                  onDismiss={() => setAnnouncement("")}
                >
                  {announcement}
                </Alert>
              )}
            {!active && editor.view !== "save" && (
              <>
                {source.rowCount === 0 && !editingPreset && (
                  <Alert type="info" header="Empty report">
                    The export will contain headers only.
                  </Alert>
                )}
                {attempted && (issues.length > 0 || columnError) && (
                  <Alert type="warning" header="Complete your settings">
                    <ul className="oms-report-download__applied-filters">
                      {issues.map((issue) => (
                        <li key={`${issue.index}:${issue.control}`}>
                          <Button
                            onClick={() => {
                              setWorkspace("rows");
                              setFocusIssue(issue);
                              setFocusFilters(true);
                            }}
                          >
                            Filter {issue.index + 1}: {issue.message}
                          </Button>
                        </li>
                      ))}
                      {columnError && (
                        <li>
                          <Button
                            onClick={() => {
                              setWorkspace("columns");
                              setFocusColumns(true);
                            }}
                          >
                            {columnError}
                          </Button>
                        </li>
                      )}
                    </ul>
                  </Alert>
                )}
                <div className="oms-export-layout">
                  <div className="oms-export-layout__editor">
                    <OMSSettingsSection
                      directSingle={Boolean(page)}
                      title={
                        editingPreset ? "Preset settings" : "Export settings"
                      }
                      ariaLabel="Report export settings"
                      activeTabId={columnsEnabled ? workspace : "rows"}
                      onChange={({ detail }) =>
                        setWorkspace(detail.activeTabId as "rows" | "columns")
                      }
                      tabs={[
                        {
                          id: "rows",
                          label: "Rows",
                          contentRenderStrategy: "lazy" as const,
                          content: (
                            <div
                              className="oms-export-workspace"
                              role="region"
                              aria-label="Rows settings"
                            >
                              <SpaceBetween size="m">
                                <FormField
                                  className="oms-export-scope"
                                  label="Rows to export"
                                  constraintText="Dashboard filters do not apply."
                                  info={
                                    <OMSHelpLink
                                      topic="rows"
                                      label="About row selection"
                                    />
                                  }
                                >
                                  <Tiles
                                    columns={2}
                                    value={mode}
                                    items={[
                                      { value: "full", label: "All rows" },
                                      {
                                        value: "filtered",
                                        label: "Filtered rows",
                                      },
                                    ]}
                                    onChange={({ detail }) => {
                                      changed();
                                      const nextMode = detail.value as
                                        | "full"
                                        | "filtered";
                                      const target =
                                        nextMode === "full" ? full : filtered;
                                      if (
                                        target.jobId ||
                                        target.sourceReport.snapshotId !==
                                          source.snapshotId
                                      )
                                        target.editSettings(source);
                                      setMode(nextMode);
                                    }}
                                  />
                                </FormField>
                                {mode === "filtered" && (
                                  <div className="oms-export-filter-settings">
                                    <OMSRecordFilters
                                      report={source}
                                      state={editableCriteria}
                                      embedded
                                      attempted={attempted}
                                      focusIssue={focusIssue}
                                      focusRequested={focusFilters}
                                      onFocusHandled={() =>
                                        setFocusFilters(false)
                                      }
                                    />
                                  </div>
                                )}
                              </SpaceBetween>
                            </div>
                          ),
                        },
                        ...(columnsEnabled
                          ? [
                              {
                                id: "columns",
                                label: "Columns",
                                contentRenderStrategy: "lazy" as const,
                                content: (
                                  <div
                                    className="oms-export-workspace oms-export-workspace--columns"
                                    role="region"
                                    aria-label="Column settings"
                                  >
                                    <OMSColumns
                                      report={source}
                                      page={Boolean(page)}
                                      settings={columns}
                                      onChange={(settings) => {
                                        changed();
                                        setColumns(settings);
                                      }}
                                      attempted={attempted}
                                      focusRequested={focusColumns}
                                      onFocusHandled={() =>
                                        setFocusColumns(false)
                                      }
                                    />
                                  </div>
                                ),
                              },
                            ]
                          : []),
                      ].filter(
                        (tab) =>
                          !page ||
                          !editingPreset ||
                          !applied ||
                          applied.scope === "both" ||
                          (applied.scope === "columns"
                            ? tab.id === "columns"
                            : tab.id === "rows")
                      )}
                    />
                  </div>
                </div>
                {!columnsEnabled && columns.strategy === "selected" && (
                  <Alert type="warning">
                    New column selections are temporarily unavailable. Your
                    selection is preserved.
                    {previousRequest && " You can retry your previous request."}
                    <Button
                      onClick={() => {
                        changed();
                        setColumns({ ...columns, strategy: "all" });
                      }}
                    >
                      Use all columns
                    </Button>
                  </Alert>
                )}
              </>
            )}
            {active && accepted && editor.view !== "save" && (
              <SpaceBetween size="m">
                {(accepted?.csvFiles || 1) > 1 && (
                  <Box color="text-body-secondary">
                    This existing ZIP contains {accepted?.csvFiles} CSV files.
                    Edit settings to prepare one CSV.
                  </Box>
                )}
                <OMSSettingsSummary
                  report={source}
                  mode={accepted.mode}
                  filters={accepted.filters}
                  operation={accepted.operation}
                  columns={accepted.outputColumns || source.columns}
                  allColumns={accepted.outputColumnStrategy !== "selected"}
                  textCaseSensitive={accepted.textCaseSensitive ?? true}
                  result
                />
              </SpaceBetween>
            )}
          </SpaceBetween>
        )}
      </div>
    </Wrapper>
  );
}
